/**
 * Dashboard voice-of-customer schema (#1340, program #1130 E2)
 *
 * Zod contract for GET /api/v1/dashboard/voice — the «Голос клиента» tab: a
 * short projection of the released section (`/analytics/voice`, #848) and of
 * the objection flow (#846), read by the same repositories with the slice's
 * window, scope and demo setting (`dashboard-quality.ts`): half-open
 * `[start, end)`, the previous window of the same length.
 *
 * Labels and counts only — no client's words, no record ids, no employees:
 * what a client said stays behind the report's own boundary. Every list is
 * cut to `VOICE_DASHBOARD_MAX_ROWS`, most conversations first, and says how
 * many rows it left out (`moreRows`; of them heard in the window —
 * `moreHeardRows`) — never a silent cut. A row with no conversation in the
 * window is sent only with its mark.
 *
 * Every section answers for itself, as the quality sections do: one source
 * failing is that section's `error`, never its neighbours', never a zero.
 */

import { z } from 'zod/v4';
import { lockedDemoOnlySchema } from './common';
import {
  dashboardQualityQuerySchema,
  dashboardQualityScopeSchema,
  dashboardSliceWindowSchema,
} from './dashboard-quality';
import { DECLINE_REASON_CODES, QUESTION_SUBJECT_CODES } from './voice-extraction';
import {
  voiceFeedbackSeveritySchema,
  voiceMarkSchema,
  voiceSignalTypeSchema,
} from './voice-of-customer';

// ============================================================================
// Query
// ============================================================================

/**
 * The quality slice's query: the same window, zone and demo setting
 * (`projectIds` read with `getAll`), and `sections`: `market` — the Summary,
 * which draws only that section, has the others not read at all (#2158). An
 * edge deployed before it ignores the parameter and answers in full.
 */
export const dashboardVoiceQuerySchema = dashboardQualityQuerySchema.extend({
  sections: z.enum(['market']).optional(),
});

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();

/** Rows a card lists; the rest is «ещё K» (`moreRows`). */
export const VOICE_DASHBOARD_MAX_ROWS = 5;

/**
 * Rows a list left out: `moreRows` — all of them, marked rows without a
 * conversation included; `moreHeardRows` — only those heard in the window,
 * the «ещё K» of a card that hides the marks (comparison off, #2533).
 */
const moreSchema = {
  moreRows: countSchema,
  moreHeardRows: countSchema,
};

/**
 * A section's state: `ok` — its data; `error` — its own source failed;
 * `noAccess` — the viewer lacks the section's own capability; `featureOff` —
 * the feature behind it is off for the organization; `notRequested` — the
 * query's `sections` left it out, and nothing was read for it.
 */
function section<T extends z.ZodType>(data: T) {
  return z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), data }),
    z.object({ status: z.literal('error') }),
    z.object({ status: z.literal('noAccess') }),
    z.object({ status: z.literal('featureOff') }),
    z.object({ status: z.literal('notRequested') }),
  ]);
}

/**
 * Conversations in this window and the previous one, and the row's mark — the
 * released report's (`voiceMarkSchema`, rule Р3): no field — no mark.
 */
const countsSchema = z.object({
  calls: countSchema,
  prevCalls: countSchema,
  mark: voiceMarkSchema.optional(),
});

/**
 * The released section's full answer: competitors, feedback by severity,
 * market signals and needs. N — conversations of the window whose current
 * analysis carries business intelligence (`callsInWindow`); needs have their
 * own N (analyses that qualify a need).
 */
export const voiceDashboardMarketSchema = z.object({
  callsInWindow: countSchema,
  prevCallsInWindow: countSchema,
  competitors: z.object({
    rows: z
      .array(
        countsSchema.extend({
          /** The canonical name (the client's glossary merges spellings). */
          name: z.string(),
          merged: z.boolean(),
          /** Mentions by tone — may add up to more than `calls`. */
          sentiment: z.object({
            positive: countSchema,
            neutral: countSchema,
            negative: countSchema,
          }),
        })
      )
      .max(VOICE_DASHBOARD_MAX_ROWS),
    ...moreSchema,
    /** The glossary could not be read: spellings are not merged. */
    mergeUnavailable: z.boolean(),
  }),
  /** All four severities, in `VOICE_FEEDBACK_SEVERITIES` order, zeros included. */
  feedback: z.array(countsSchema.extend({ severity: voiceFeedbackSeveritySchema })),
  /** All five signal types, in `VOICE_SIGNAL_TYPES` order, zeros included. */
  signals: z.array(countsSchema.extend({ type: voiceSignalTypeSchema })),
  needs: countsSchema.extend({
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
  }),
  /**
   * The counts are a lower bound: a window held more conversations than one
   * scan reads, and the scan counted them. Counted from the facts the rows
   * cover the whole window — then see `sentimentSampled` (#2596).
   */
  truncated: z.boolean(),
  /**
   * The rows are counted from the facts over the whole window, but the
   * competitors' tone comes from the capped scan's latest conversations: the
   * card says so beside the tone (#2596).
   */
  sentimentSampled: z.boolean(),
  prevTruncated: z.boolean(),
});

/** The company's conversation themes (#845). N — conversations the step assigned themes to. */
export const voiceDashboardThemesSchema = z.object({
  analyzedCalls: countSchema,
  assignedCalls: countSchema,
  prevAssignedCalls: countSchema,
  rows: z
    .array(
      z.object({
        code: z.string(),
        name: z.string(),
        calls: countSchema,
        /** A theme's own N — conversations since the theme's base date. */
        callsInWindow: countSchema,
        /** `null` — the previous window is not comparable (the theme is newer). */
        prevCalls: countSchema.nullable(),
        prevCallsInWindow: countSchema.nullable(),
        mark: voiceMarkSchema.optional(),
      })
    )
    .max(VOICE_DASHBOARD_MAX_ROWS),
  ...moreSchema,
  /** «Без темы»: no theme of the set fitted. */
  uncategorized: z.object({
    calls: countSchema,
    prevCalls: countSchema.nullable(),
    mark: voiceMarkSchema.optional(),
  }),
  truncated: z.boolean(),
  prevTruncated: z.boolean(),
});

/** Customer questions and decline reasons (#843/#844), without their breakdowns. */
export const voiceDashboardQuestionsSchema = z.object({
  /** Conversations of the window with a current analysis and, of them, where the step ran. */
  analyzedCalls: countSchema,
  collectedCalls: countSchema,
  /** The organization's first conversation with the step's result; `null` — none yet. */
  dataSince: z.string().nullable(),
  questions: z.object({
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
    rows: z
      .array(
        countsSchema.extend({
          code: z.enum(QUESTION_SUBJECT_CODES),
          /** Of them, a question of the subject was left without a full answer. */
          unansweredCalls: countSchema,
        })
      )
      .max(VOICE_DASHBOARD_MAX_ROWS),
    ...moreSchema,
  }),
  declines: z.object({
    /** N — conversations of the window that ended in a decline. */
    callsInWindow: countSchema,
    prevCallsInWindow: countSchema,
    rows: z
      .array(
        countsSchema.extend({
          code: z.enum(DECLINE_REASON_CODES),
          /** Of them, the client named the reason themselves. */
          statedCalls: countSchema,
        })
      )
      .max(VOICE_DASHBOARD_MAX_ROWS),
    ...moreSchema,
  }),
  truncated: z.boolean(),
  prevTruncated: z.boolean(),
});

/** Purchase barriers — objection codes by frequency (#846); no previous window. */
export const voiceDashboardBarriersSchema = z.object({
  codes: z
    .array(z.object({ code: z.string(), calls: countSchema }))
    .max(VOICE_DASHBOARD_MAX_ROWS),
  moreRows: countSchema,
  /** Conversations with any objection in the window — the denominator. */
  scannedCalls: countSchema,
  truncated: z.boolean(),
});

export const dashboardVoiceResponseSchema = z.object({
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  /**
   * The demo setting the answer READ: `yes` / `no` as asked, or `only` on a
   * demo-only answer of a locked organization (#2816) — what was read, not
   * what was requested.
   */
  includeDemo: z.enum(['yes', 'no', 'only']),
  demoOnly: lockedDemoOnlySchema,
  sections: z.object({
    market: section(voiceDashboardMarketSchema),
    themes: section(voiceDashboardThemesSchema),
    questions: section(voiceDashboardQuestionsSchema),
    barriers: section(voiceDashboardBarriersSchema),
  }),
});

// ============================================================================
// Types
// ============================================================================

export type DashboardVoiceQuery = z.infer<typeof dashboardVoiceQuerySchema>;
export type VoiceDashboardMarket = z.infer<typeof voiceDashboardMarketSchema>;
export type VoiceDashboardThemes = z.infer<typeof voiceDashboardThemesSchema>;
export type VoiceDashboardQuestions = z.infer<typeof voiceDashboardQuestionsSchema>;
export type VoiceDashboardBarriers = z.infer<typeof voiceDashboardBarriersSchema>;
export type DashboardVoiceResponse = z.infer<typeof dashboardVoiceResponseSchema>;
